import { FC, useContext, useEffect } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../context/notificationContext";
import useI18n from "../../../hooks/useTranslations";
import Button from "../../ui/Button";
import Form from "../../ui/Form";
import FormField from "../../ui/FormField";
import LoadingSpinner from "../../ui/LoadingSpinner";
import Numeric from "../../ui/Numeric";
import Switch from "../../ui/Switch";
import styles from "./BorderAccessories.module.css";

export type BorderAccessoriesFormBody = {
  wallTerminalQuantity: number;
  nonWallTerminalQuantity: number;
  waterproofKit: boolean;
  gateQuantity: number | null;
};

type BorderAccessoriesProps = {
  defaultValues?: BorderAccessoriesFormBody;
  canEdit?: boolean;
  isLoading?: boolean;
  next: () => void;
  back: () => void;
  isAdmin?: boolean;
  showWaterproofKit?: boolean;
  onSubmit: (body: BorderAccessoriesFormBody) => void | Promise<void>;
};

const BorderAccessories: FC<BorderAccessoriesProps> = ({
  defaultValues,
  canEdit,
  isLoading,
  next,
  back,
  isAdmin,
  showWaterproofKit,
  onSubmit,
}) => {
  const i18n = useI18n();
  const dispatch = useContext(NotificationDispatch);
  const normalizeQuantity = (value: number | null) => Math.max(0, Math.ceil(value ?? 0));

  return (
    <Form<BorderAccessoriesFormBody>
      mode={"onBlur"}
      defaultValues={defaultValues}
      disabled={isLoading}
      onSubmit={() => async (formData) => {
        const body = {
          ...formData,
          wallTerminalQuantity: normalizeQuantity(formData.wallTerminalQuantity),
          nonWallTerminalQuantity: normalizeQuantity(formData.nonWallTerminalQuantity),
          gateQuantity: formData.gateQuantity === null ? null : normalizeQuantity(formData.gateQuantity),
        };

        if (isAdmin || canEdit) {
          try {
            await onSubmit(body);
            dispatch(showSuccessNotification(i18n.translation));
          } catch (error) {
            dispatch(showErrorNotification(i18n, error));
          }
        } else {
          next();
        }
      }}
      submitText={isAdmin ? i18n.translation.common.save : i18n.translation.common.next}
      secondaryButton={
        <>
          {isLoading && <LoadingSpinner delayed={0} size="small" />}
          <Button
            buttonProps={{
              disabled: isLoading,
              onClick: back,
            }}
            type="secondary"
          >
            {i18n.translation.common.back}
          </Button>
        </>
      }
    >
      {({ control, setValue, reset }) => {
        useEffect(() => {
          defaultValues && reset(defaultValues);
        }, [defaultValues]);

        return (
          <div className={styles.form}>
            <Controller
              name="wallTerminalQuantity"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label="Wall terminals" error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={field.disabled || isLoading || !canEdit}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("wallTerminalQuantity", normalizeQuantity(value))}
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="nonWallTerminalQuantity"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label="Non-wall terminals" error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={field.disabled || isLoading || !canEdit}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("nonWallTerminalQuantity", normalizeQuantity(value))}
                    />
                  )}
                </FormField>
              )}
            />
            {showWaterproofKit && (
              <Controller
                name="waterproofKit"
                control={control}
                render={({ field, fieldState }) => (
                  <FormField label="Waterproof kit" error={fieldState.error} isOptional>
                    {({ labelId }) => (
                      <Switch
                        id={labelId}
                        checked={!!field.value}
                        disabled={field.disabled || isLoading || !canEdit}
                        onChange={(checked) => setValue("waterproofKit", checked)}
                      />
                    )}
                  </FormField>
                )}
              />
            )}
            <Controller
              name="gateQuantity"
              control={control}
              render={({ field, fieldState }) => (
                <FormField label="Gates" error={fieldState.error} isOptional>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={field.disabled || isLoading || !canEdit}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("gateQuantity", value === null ? null : normalizeQuantity(value))}
                    />
                  )}
                </FormField>
              )}
            />
          </div>
        );
      }}
    </Form>
  );
};

export default BorderAccessories;
